package dev.lingvo.shared.screenshots.templates

import dev.lingvo.shared.screenshots.doc.BackgroundLayer
import dev.lingvo.shared.screenshots.doc.CanvasSize
import dev.lingvo.shared.screenshots.doc.Box
import dev.lingvo.shared.screenshots.doc.GroupLayer
import dev.lingvo.shared.screenshots.doc.Layer
import dev.lingvo.shared.screenshots.doc.ImageFit
import dev.lingvo.shared.screenshots.doc.ImageLayer
import dev.lingvo.shared.screenshots.doc.ImageSource
import dev.lingvo.shared.screenshots.doc.LayerBase
import dev.lingvo.shared.screenshots.doc.Page
import dev.lingvo.shared.screenshots.doc.Paint
import dev.lingvo.shared.screenshots.doc.Role
import dev.lingvo.shared.screenshots.doc.ShapeKind
import dev.lingvo.shared.screenshots.doc.ShapeLayer
import dev.lingvo.shared.screenshots.doc.Stop
import dev.lingvo.shared.screenshots.scene.namespaceDigest

private const val ARTWORK_NAME = "Background artwork"
private const val SCRIM_NAME = "Background artwork scrim"
private fun artworkId(page: Page, suffix: String): String {
    val id = page.layers.firstOrNull()?.base?.id ?: page.id
    return if (id.length <= 22) "$id-$suffix" else "a-${namespaceDigest("$suffix:$id")}"
}
private fun imageId(page: Page): String = artworkId(page, "background-image")
private fun scrimId(page: Page): String = artworkId(page, "background-scrim")

/** Uses template background bounds; imported documents can supply an explicit canvas through the overload. */
fun withBackgroundImage(page: Page, assetId: String, scrim: Boolean): Page {
    val background = page.layers.firstOrNull() as? BackgroundLayer
    require(background != null) { "Background artwork requires a BackgroundLayer at index zero" }
    return addArtwork(page, assetId, scrim, Box(0.0, 0.0, background.base.box.w, background.base.box.h))
}

private fun addArtwork(page: Page, assetId: String, scrim: Boolean, bounds: Box): Page {
    require(Regex("^[a-z0-9][a-z0-9-]{0,63}$").matches(assetId)) { "Invalid background asset id" }
    val managedIds = page.layers.filter { managedArtwork(page, it) }.map { it.base.id }.toSet()
    val clean = page.copy(layers = page.layers.filterNot { managedArtwork(page, it) })
    val background = clean.layers.firstOrNull() as? BackgroundLayer
    require(background != null) { "Background artwork requires a BackgroundLayer at index zero" }
    fun collision(layer: Layer): Boolean = layer.base.id == imageId(page) || layer.base.id == scrimId(page) ||
        layer is GroupLayer && layer.children.any(::collision)
    require(clean.layers.none(::collision)) { "Background artwork id collision" }
    val image = ImageLayer(LayerBase(imageId(page), ARTWORK_NAME, bounds), ImageSource.Asset(assetId), ImageFit.COVER)
    val layers = clean.layers.toMutableList()
    layers.add(1, image)
    if (scrim) {
        val captionIndex = layers.indexOfFirst { it.base.role == Role.CAPTION && !it.base.hidden }
        if (captionIndex >= 0) {
            val boxes = layers.filter { it.base.role == Role.CAPTION && !it.base.hidden }.map { it.base.box }
            val left = boxes.minOf { it.x }
            val top = boxes.minOf { it.y }
            val scrimBox = Box(left, top, boxes.maxOf { it.x + it.w } - left, boxes.maxOf { it.y + it.h } - top)
            layers.add(captionIndex, ShapeLayer(LayerBase(scrimId(page), SCRIM_NAME, scrimBox), ShapeKind.RECT,
                fill = Paint.Linear(180.0, listOf(Stop(0.0, "@bg/0.96"), Stop(1.0, "@bg/0.92")))))
        }
    }
    return pruneArtworkPatches(clean.copy(layers = layers), managedIds - layers.map { it.base.id }.toSet())
}

private fun managedArtwork(page: Page, layer: Layer): Boolean =
    layer.base.id == imageId(page) && layer.base.name == ARTWORK_NAME && layer is ImageLayer ||
        layer.base.id == scrimId(page) && layer.base.name == SCRIM_NAME && layer is ShapeLayer

private fun pruneArtworkPatches(page: Page, removed: Set<String>): Page = page.copy(overrides = page.overrides.mapValues { (_, override) ->
    override.copy(patches = override.patches.filterKeys { it !in removed })
})

/** Removes only helper-owned artwork and its locale patches, preserving unrelated user edits. */
fun withoutBackgroundImage(page: Page): Page {
    val removed = page.layers.filter { managedArtwork(page, it) }.map { it.base.id }.toSet()
    return pruneArtworkPatches(page.copy(layers = page.layers.filterNot { managedArtwork(page, it) }), removed)
}

/** Explicit store dimensions let imported documents keep their ignored BackgroundLayer box unchanged. */
fun withBackgroundImage(page: Page, assetId: String, scrim: Boolean, canvas: CanvasSize): Page {
    require(canvas.width > 0 && canvas.height > 0)
    return addArtwork(page, assetId, scrim, Box(0.0, 0.0, canvas.width.toDouble() * page.slotIds.size, canvas.height.toDouble()))
}
